import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

export type OrganizationAcceptedMember = {
  membershipId: string;
  volunteerId: string;
  joinedAt: string;
  role: 'member' | 'admin';
  firstName: string | null;
  lastName: string | null;
  profileImage: string | null;
};

function mapRows(
  memberships: { id: string; volunteer_id: string; updated_at: string; role: string }[],
  profilesById: Map<
    string,
    { first_name: string | null; last_name: string | null; profile_image: string | null }
  >
): OrganizationAcceptedMember[] {
  return memberships.map((m) => {
    const p = profilesById.get(m.volunteer_id);
    return {
      membershipId: m.id,
      volunteerId: m.volunteer_id,
      joinedAt: m.updated_at,
      role: m.role === 'admin' ? 'admin' : 'member',
      firstName: p?.first_name ?? null,
      lastName: p?.last_name ?? null,
      profileImage: p?.profile_image ?? null,
    };
  });
}

export function useOrganizationAcceptedMembers(organizationUserId: string | null) {
  const [members, setMembers] = useState<OrganizationAcceptedMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!organizationUserId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: memRows, error: memError } = await supabase
        .from('organization_memberships')
        .select('id, volunteer_id, updated_at, role')
        .eq('organization_id', organizationUserId)
        .eq('status', 'accepted')
        .order('updated_at', { ascending: false });

      if (memError) throw memError;

      const list = (memRows ?? []) as {
        id: string;
        volunteer_id: string;
        updated_at: string;
        role: string;
      }[];
      if (list.length === 0) {
        setMembers([]);
        return;
      }

      const ids = [...new Set(list.map((r) => r.volunteer_id))];
      const { data: profRows, error: profError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, profile_image')
        .in('id', ids);

      if (profError) throw profError;

      const profilesById = new Map<
        string,
        { first_name: string | null; last_name: string | null; profile_image: string | null }
      >();
      for (const row of profRows ?? []) {
        const r = row as {
          id: string;
          first_name: string | null;
          last_name: string | null;
          profile_image: string | null;
        };
        profilesById.set(r.id, {
          first_name: r.first_name,
          last_name: r.last_name,
          profile_image: r.profile_image,
        });
      }

      setMembers(mapRows(list, profilesById));
    } catch (e) {
      logger.error('useOrganizationAcceptedMembers', e);
      setError(e instanceof Error ? e.message : 'Failed to load members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [organizationUserId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  return {
    members,
    loading,
    error,
    refetch: fetchMembers,
  };
}
